/**
 * Post Account creation errors are errors in which the user can no longer
 * finish the flow on signup-lite. This may be because of something like an
 * expired token, in which case we'll show the generic error page, or it
 * could be something that requires a redirect to another app to complete
 * the transaction.
 */

import { appStore } from '@/store';
import { $logger } from '@/lib/logger';
import { redirectToGenericError } from '../../utils';

/**
 * GENERIC_ERRORS
 * These are errors that the user will encounter irrespective of which
 * app they are using. For example, an invalid token is invalid on
 * signup-lite, xoon, and hermes.
 */
const GENERIC_ERRORS = {
  // ACCOUNT_CANNOT_BE_FETCHED: '',
  // BLOCK_NON_DOMESTIC_SHIPPING: '',
  // BLOCK_PPC_INELIGIBLE_USER: '',
  COMPLIANCE_VIOLATION: redirectToGenericError,
  // CREDIT_CARD_AUTH_FAILED: '',
  CREDIT_PAYMENT_NOT_ALLOWED: redirectToGenericError,
  // CURRENCY_COMPLIANCE: '',
  // DOMESTIC_TRANSACTION_REQUIRED: '',
  DUPLICATE_TRANSACTION: redirectToGenericError,
  // EXPIRED_SESSION: '',
  // GAMER_ACCOUNT_UNVERIFIED: '',
  // GAMER_INELIGIBLE: '',
  // GAMER_FI_INELIGIBLE: '',
  // GAMER_FI_UNCONFIRMED: '',
  // GAMER_INVALID_ACCOUNT_TYPE: '',
  // GAMER_LOGIN_OUT_OF_COUNTRY: '',
  // GAMER_NO_VALID_FUNDING_INSTRUMENT: '',
  // GAMER_RESIDENCE_OUT_OF_COUNTRY: '',
  // GAMING_FUNDING_SOURCE_VALIDATION_FAILED: '',
  // GUEST_CHECKOUT_NOT_ALLOWED: '',
  // HERMES_PAYER_COUNTRY_NOT_ALLOWED_DCF: '',
  INITIAL_DATA_ERROR: redirectToGenericError,
  INTERNAL_SERVICE_ERROR: redirectToGenericError,
  INVALID_ARGUMENT: redirectToGenericError,
  INVALID_FUNDING_OPTION: redirectToGenericError,
  INVALID_RESOURCE_ID: redirectToGenericError,
  INVALID_TOKEN: redirectToGenericError,
  NEGATIVE_BALANCE_TRANSACTION_REFUSED: redirectToGenericError,
  PAYEE_ACCOUNT_LOCKED_OR_CLOSED: redirectToGenericError,
  // PAYEE_ACCOUNT_RESTRICTED: '',
  PAYEE_BLOCKED_TRANSACTION: redirectToGenericError,
  PAYER_CANNOT_PAY: redirectToGenericError,
  PAYER_COUNTRY_NOT_ALLOWED: redirectToGenericError,
  // PAYER_COUNTRY_NOT_ENABLED: '',
  PAYER_INVALID_FOR_PAYMENT: redirectToGenericError,
  PAYER_LOGIN_COUNTRY_NOT_ALLOWED: redirectToGenericError,
  PAYMENT_ALREADY_DONE: redirectToGenericError,
  PAYMENT_CANNOT_BE_INITIATED: redirectToGenericError,
  PAYMENT_DENIED: redirectToGenericError,
  // PAYMENT_PROCESS_ERROR: '',
  PERMISSION_DENIED: redirectToGenericError,
  // RESTRICTED_USER: '',
  R_ERROR: redirectToGenericError,
  RETRY: redirectToGenericError,
  RISK_DENIED: redirectToGenericError,
  SENDING_LIMIT_EXCEEDED: redirectToGenericError,
  // STUDENT_ACCOUNT: '',
  TRANSACTION_REFUSED: redirectToGenericError,
  // UNSUPPORTED_COUNTRY: '',
  // UNSUPPORTED_DUE_TO_RECEIVER_PREFERENCE: '',
  // UNSUPPORTED_PAYEE_CURRENCY: '',
  VERIFY_USER_IDENTITY: redirectToGenericError,
  // XOON_PAYER_COUNTRY_NOT_ALLOWED_DCF: '',
};

/**
 * If a member account is created then these errors result in a
 * redirect to the member app and if it was guest checkout then
 * we show a page level error and ask the user to fix the issue.
 */
const POST_ACCOUNT_CREATION_ERRORS = {
  ADD_CARD_CONTINGENCY: error => {
    $logger.warn('weasley_ADD_CARD_CONTINGENCY_error', { error });
    return 'pageLevelError.cardGenericError';
  },
  BIN_BLOCKED_OR_CARD_NOT_ALLOWED: () => 'pageLevelError.cardNotAllowed',
  CARD_GENERIC_ERROR: () => 'pageLevelError.cardGenericError',
  CC_PROCESSOR_DECLINED: () => 'pageLevelError.cardDecline',
  INSTRUMENT_DECLINED: () => 'pageLevelError.cardDecline',
  INVALID_EXPIRATION: () => 'pageLevelError.invalidExpiry',
  INVALID_SECURITY_CODE: () => 'pageLevelError.invalidSecurityCode',
  ISSUER_DECLINE: () => 'pageLevelError.cardIssuerDecline',
  NEED_CONFIRMED_CARD: () => 'pageLevelError.cardInternalError',
  NEED_CREDIT_CARD: () => 'pageLevelError.cardDecline',
  NEED_CREDIT_CARD_OR_BANK_ACCOUNT: () => {
    const { ui: uiState } = appStore.getState();
    const { fiType } = uiState;

    if (fiType === 'bank') {
      return 'pageLevelError.cannotUseBank';
    }
    return 'pageLevelError.cardDecline';
  },
};

export { GENERIC_ERRORS, POST_ACCOUNT_CREATION_ERRORS };
